import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { publicApi } from '../services/api.jsx';
import './Events.css';

const QUESTION_TYPES_WITH_OPTIONS = ['select', 'radio', 'checkbox'];
const RSVP_PAGE_LOGO_URL = import.meta.env.VITE_RSVP_LOGO_URL || 'https://rscwebsitestorage.blob.core.windows.net/$web/RSC Base Logo_1024x600_NEW.png';
const WINDOWS_TO_IANA_TIMEZONE = {
  'Eastern Standard Time': 'America/New_York',
  'Central Standard Time': 'America/Chicago',
  'Mountain Standard Time': 'America/Denver',
  'Pacific Standard Time': 'America/Los_Angeles',
  UTC: 'UTC'
};

const getDatePartsInTimeZone = (date, timeZone) => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });

  const parts = formatter.formatToParts(date).reduce((acc, part) => {
    if (part.type !== 'literal') acc[part.type] = Number(part.value);
    return acc;
  }, {});

  return {
    year: parts.year,
    month: parts.month,
    day: parts.day,
    hour: parts.hour,
    minute: parts.minute,
    second: parts.second
  };
};

const getUtcDateForWallClockInTimeZone = (components, timeZone) => {
  let utcMillis = Date.UTC(
    components.year,
    components.month - 1,
    components.day,
    components.hour,
    components.minute,
    components.second || 0
  );

  for (let i = 0; i < 2; i += 1) {
    const zonedParts = getDatePartsInTimeZone(new Date(utcMillis), timeZone);
    const zonedMillis = Date.UTC(
      zonedParts.year,
      zonedParts.month - 1,
      zonedParts.day,
      zonedParts.hour,
      zonedParts.minute,
      zonedParts.second || 0
    );
    const targetMillis = Date.UTC(
      components.year,
      components.month - 1,
      components.day,
      components.hour,
      components.minute,
      components.second || 0
    );
    utcMillis += (targetMillis - zonedMillis);
  }

  return new Date(utcMillis);
};

const parseEventDate = (value, eventTimeZone) => {
  if (!value) return null;
  const ianaTimeZone = getIanaTimeZone(eventTimeZone);

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    const wallClock = {
      year: value.getUTCFullYear(),
      month: value.getUTCMonth() + 1,
      day: value.getUTCDate(),
      hour: value.getUTCHours(),
      minute: value.getUTCMinutes(),
      second: value.getUTCSeconds()
    };
    return ianaTimeZone
      ? getUtcDateForWallClockInTimeZone(wallClock, ianaTimeZone)
      : new Date(
        wallClock.year,
        wallClock.month - 1,
        wallClock.day,
        wallClock.hour,
        wallClock.minute,
        wallClock.second,
        value.getUTCMilliseconds()
      );
  }

  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/);
    if (match) {
      const [, year, month, day, hour, minute, second] = match;
      const wallClock = {
        year: Number(year),
        month: Number(month),
        day: Number(day),
        hour: Number(hour),
        minute: Number(minute),
        second: Number(second || 0)
      };
      return ianaTimeZone
        ? getUtcDateForWallClockInTimeZone(wallClock, ianaTimeZone)
        : new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second || 0)
      );
    }

    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }

  return null;
};

const getIanaTimeZone = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized) return null;
  return WINDOWS_TO_IANA_TIMEZONE[normalized] || normalized;
};

const formatCalendarDateInTimeZone = (date, timeZone) => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });

  const parts = formatter.formatToParts(date).reduce((acc, part) => {
    if (part.type !== 'literal') acc[part.type] = part.value;
    return acc;
  }, {});

  return `${parts.year}${parts.month}${parts.day}T${parts.hour}${parts.minute}${parts.second}`;
};

const formatCalendarDate = (date) => {
  const pad = (value) => String(value).padStart(2, '0');
  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate())
  ].join('') +
  'T' +
  [
    pad(date.getHours()),
    pad(date.getMinutes()),
    pad(date.getSeconds())
  ].join('');
};

const formatCalendarDateUtc = (date) => {
  const pad = (value) => String(value).padStart(2, '0');
  return [
    date.getUTCFullYear(),
    pad(date.getUTCMonth() + 1),
    pad(date.getUTCDate())
  ].join('') +
  'T' +
  [
    pad(date.getUTCHours()),
    pad(date.getUTCMinutes()),
    pad(date.getUTCSeconds())
  ].join('') +
  'Z';
};

const buildCalendarLinks = (eventInfo) => {
  if (!eventInfo?.EventDate) return null;

  const start = parseEventDate(eventInfo.EventDate, eventInfo.EventTimeZone);
  const endCandidate = parseEventDate(eventInfo.EventEndDate, eventInfo.EventTimeZone);
  if (!start || Number.isNaN(start.getTime())) return null;
  const fallbackEnd = new Date(start.getTime() + 60 * 60 * 1000);
  const end = endCandidate && !Number.isNaN(endCandidate.getTime()) ? endCandidate : fallbackEnd;

  if (end.getTime() <= start.getTime()) {
    end.setTime(fallbackEnd.getTime());
  }

  const title = eventInfo.Title || 'Event';
  const details = eventInfo.Description || '';
  const location = eventInfo.Location || '';
  const ianaTimeZone = getIanaTimeZone(eventInfo.EventTimeZone);

  const googleDates = ianaTimeZone
    ? `${formatCalendarDateInTimeZone(start, ianaTimeZone)}/${formatCalendarDateInTimeZone(end, ianaTimeZone)}`
    : `${formatCalendarDateUtc(start)}/${formatCalendarDateUtc(end)}`;

  const google = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${encodeURIComponent(googleDates)}${ianaTimeZone ? `&ctz=${encodeURIComponent(ianaTimeZone)}` : ''}&details=${encodeURIComponent(details)}&location=${encodeURIComponent(location)}`;

  const outlook = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${encodeURIComponent(title)}&startdt=${encodeURIComponent(start.toISOString())}&enddt=${encodeURIComponent(end.toISOString())}${ianaTimeZone ? `&ctz=${encodeURIComponent(ianaTimeZone)}` : ''}&body=${encodeURIComponent(details)}&location=${encodeURIComponent(location)}`;

  const icsDateStart = ianaTimeZone ? formatCalendarDateInTimeZone(start, ianaTimeZone) : formatCalendarDateUtc(start);
  const icsDateEnd = ianaTimeZone ? formatCalendarDateInTimeZone(end, ianaTimeZone) : formatCalendarDateUtc(end);
  const icsDateStartProp = ianaTimeZone ? `DTSTART;TZID=${ianaTimeZone}:${icsDateStart}` : `DTSTART:${icsDateStart}`;
  const icsDateEndProp = ianaTimeZone ? `DTEND;TZID=${ianaTimeZone}:${icsDateEnd}` : `DTEND:${icsDateEnd}`;

  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'PRODID:-//Bulk Email Tool//Event RSVP//EN',
    'BEGIN:VEVENT',
    `UID:${start.getTime()}-${Math.random().toString(36).slice(2)}`,
    `DTSTAMP:${formatCalendarDateUtc(new Date())}`,
    icsDateStartProp,
    icsDateEndProp,
    `SUMMARY:${title}`,
    `DESCRIPTION:${details.replace(/\n/g, '\\n')}`,
    `LOCATION:${location}`,
    'END:VEVENT',
    'END:VCALENDAR'
  ].join('\r\n');

  const icsUrl = `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`;

  return { google, outlook, icsUrl };
};

const EventRsvp = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [wasUpdated, setWasUpdated] = useState(false);
  const [eventInfo, setEventInfo] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    company: '',
    phone: '',
    notes: ''
  });
  const [answers, setAnswers] = useState({});
  const hasDeclineIntent = searchParams.get('intent') === 'decline' || searchParams.get('decline') === '1';
  const [showFullRsvpForm, setShowFullRsvpForm] = useState(!hasDeclineIntent);

  useEffect(() => {
    const loadRsvp = async () => {
      try {
        setLoading(true);
        const response = await publicApi.get(`/public/events/rsvp/${token}`);
        setEventInfo(response.data.event);
        setQuestions(response.data.questions || []);
        setFormData(prev => ({
          ...prev,
          email: response.data.contact?.Email || '',
          firstName: response.data.contact?.FirstName || '',
          lastName: response.data.contact?.LastName || '',
          company: response.data.contact?.Company || '',
          phone: response.data.contact?.Phone || '',
          notes: response.data.contact?.Notes || ''
        }));

        const answersMap = (response.data.existingAnswers || []).reduce((acc, answer) => {
          const question = (response.data.questions || []).find(q => q.QuestionId === answer.QuestionId);
          if (!question) return acc;

          if (question.QuestionType === 'checkbox') {
            acc[answer.QuestionId] = (answer.AnswerText || '')
              .split(';')
              .map(item => item.trim())
              .filter(Boolean);
          } else {
            acc[answer.QuestionId] = answer.AnswerText || '';
          }

          return acc;
        }, {});

        setAnswers(answersMap);
        setError('');
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load RSVP details');
      } finally {
        setLoading(false);
      }
    };

    loadRsvp();
  }, [token]);

  const handleDecline = async () => {
    const confirmed = window.confirm('Are you sure you want to decline this invitation?');
    if (!confirmed) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      const response = await publicApi.post(`/public/events/rsvp/${token}/decline`);
      setWasUpdated(Boolean(response.data?.registration));
      setSuccess(response.data.status);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to record decline');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    setShowFullRsvpForm(!hasDeclineIntent);
  }, [hasDeclineIntent]);

  const formattedEventDate = useMemo(() => {
    if (!eventInfo?.EventDate) return '';
    const parsed = parseEventDate(eventInfo.EventDate, eventInfo.EventTimeZone);
    if (!parsed || Number.isNaN(parsed.getTime())) return '';
    const ianaTimeZone = getIanaTimeZone(eventInfo.EventTimeZone);
    try {
      return new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: ianaTimeZone || undefined,
        timeZoneName: ianaTimeZone ? 'short' : undefined
      }).format(parsed);
    } catch {
      return parsed.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }, [eventInfo]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleCheckboxChange = (questionId, optionText, checked) => {
    setAnswers(prev => {
      const current = Array.isArray(prev[questionId]) ? prev[questionId] : [];
      if (checked) {
        return { ...prev, [questionId]: [...current, optionText] };
      }
      return { ...prev, [questionId]: current.filter(item => item !== optionText) };
    });
  };

  const validateRequiredQuestions = () => {
    for (const question of questions) {
      if (!question.IsRequired) continue;
      const value = answers[question.QuestionId];
      if (Array.isArray(value) && value.length === 0) return false;
      if (!Array.isArray(value) && !value) return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email) {
      setError('Email is required');
      return;
    }

    if (eventInfo?.RequireFirstName && !formData.firstName.trim()) {
      setError('First name is required');
      return;
    }

    if (eventInfo?.RequireLastName && !formData.lastName.trim()) {
      setError('Last name is required');
      return;
    }

    if (eventInfo?.RequireCompany && !formData.company.trim()) {
      setError('Company is required');
      return;
    }

    if (eventInfo?.RequirePhone && !formData.phone.trim()) {
      setError('Phone is required');
      return;
    }

    if (!validateRequiredQuestions()) {
      setError('Please answer all required questions');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const answersPayload = questions.map(question => {
        const value = answers[question.QuestionId];
        if (!value && question.IsRequired) return null;
        if (!value) return null;
        const answerText = Array.isArray(value) ? value.join('; ') : value;
        return {
          questionId: question.QuestionId,
          answerText
        };
      }).filter(Boolean);

      const response = await publicApi.post(`/public/events/rsvp/${token}`, {
        ...formData,
        answers: answersPayload
      });

      setWasUpdated(Boolean(response.data?.wasUpdated));
      setSuccess(response.data.status);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit RSVP');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="loading">Loading RSVP...</div>;

  if (error && !eventInfo) {
    return (
      <div className="rsvp-container">
        <div className="rsvp-card">
          <img src={RSVP_PAGE_LOGO_URL} alt="RSC" className="rsvp-logo" />
          <h2>RSVP Unavailable</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (success) {
    const calendarLinks = buildCalendarLinks(eventInfo);
    return (
      <div className="rsvp-container">
        <div className="rsvp-card">
          <img src={RSVP_PAGE_LOGO_URL} alt="RSC" className="rsvp-logo" />
          <h2>{success === 'declined' ? 'RSVP Declined' : 'RSVP Confirmed'}</h2>
          <p>
            {success === 'declined'
              ? 'Your decline has been recorded.'
              : wasUpdated
                ? 'Your RSVP has been updated.'
                : 'Your RSVP has been recorded.'}
          </p>
          <p>Status: <strong>{success}</strong></p>
          {success !== 'declined' && calendarLinks && (
            <div className="calendar-links">
              <h3>Add to your calendar</h3>
              <div className="calendar-buttons">
                <a className="btn btn-secondary" href={calendarLinks.google} target="_blank" rel="noopener noreferrer">
                  Google Calendar
                </a>
                <a className="btn btn-secondary" href={calendarLinks.outlook} target="_blank" rel="noopener noreferrer">
                  Outlook Calendar
                </a>
                <a className="btn btn-secondary" href={calendarLinks.icsUrl} download="event.ics">
                  Download ICS
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (hasDeclineIntent && !showFullRsvpForm) {
    return (
      <div className="rsvp-container">
        <div className="rsvp-card rsvp-card-compact">
          <img src={RSVP_PAGE_LOGO_URL} alt="RSC" className="rsvp-logo" />
          <div className="rsvp-intent-badge">Decline Invitation</div>
          <h1>{eventInfo?.Title}</h1>
          <p className="rsvp-intent-copy">
            You are about to decline this invitation. Your RSVP will only be changed if you confirm below.
          </p>

          <div className="rsvp-details rsvp-details-card">
            <div><strong>Date:</strong> {formattedEventDate}</div>
            {eventInfo?.Location && <div><strong>Location:</strong> {eventInfo.Location}</div>}
            {eventInfo?.VirtualLink && (
              <div>
                <strong>Virtual Link:</strong>{' '}
                <a href={eventInfo.VirtualLink} target="_blank" rel="noopener noreferrer">{eventInfo.VirtualLink}</a>
              </div>
            )}
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="rsvp-intent-actions">
            <button
              type="button"
              className="btn btn-danger"
              disabled={submitting}
              onClick={handleDecline}
            >
              {submitting ? 'Submitting...' : 'Decline Invitation'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={submitting}
              onClick={() => setShowFullRsvpForm(true)}
            >
              Review Event Details
            </button>
          </div>

          <p className="rsvp-intent-footnote">
            Want to keep or update your RSVP instead? Review the full RSVP form before taking action.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rsvp-container">
      <div className="rsvp-card">
        <img src={RSVP_PAGE_LOGO_URL} alt="RSC" className="rsvp-logo" />
        <h1>{eventInfo?.Title}</h1>
        {eventInfo?.Description && <p className="event-description">{eventInfo.Description}</p>}

        <div className="rsvp-details">
          <div><strong>Date:</strong> {formattedEventDate}</div>
          {eventInfo?.Location && <div><strong>Location:</strong> {eventInfo.Location}</div>}
          {eventInfo?.VirtualLink && (
            <div>
              <strong>Virtual Link:</strong>{' '}
              <a href={eventInfo.VirtualLink} target="_blank" rel="noopener noreferrer">{eventInfo.VirtualLink}</a>
            </div>
          )}
        </div>

        {hasDeclineIntent && (
          <div className="warning-message" style={{ marginBottom: '12px' }}>
            This decline link now requires confirmation. You can decline below or review the RSVP details first.
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="rsvp-form">
          <div className="form-grid">
            <div className="form-group">
              <label>Email *</label>
              <input type="email" name="email" value={formData.email} onChange={handleInputChange} required />
            </div>
            <div className="form-group">
              <label>First Name{eventInfo?.RequireFirstName ? ' *' : ''}</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                required={Boolean(eventInfo?.RequireFirstName)}
              />
            </div>
            <div className="form-group">
              <label>Last Name{eventInfo?.RequireLastName ? ' *' : ''}</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                required={Boolean(eventInfo?.RequireLastName)}
              />
            </div>
            <div className="form-group">
              <label>Company{eventInfo?.RequireCompany ? ' *' : ''}</label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                required={Boolean(eventInfo?.RequireCompany)}
              />
            </div>
            <div className="form-group">
              <label>Phone{eventInfo?.RequirePhone ? ' *' : ''}</label>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required={Boolean(eventInfo?.RequirePhone)}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Let us know if you have any questions:</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={3}
            />
          </div>

          {questions.length > 0 && (
            <div className="questions-section">
              <h3>Additional Questions</h3>
              {questions.map(question => (
                <div key={question.QuestionId} className="form-group">
                  <label>
                    {question.QuestionText}
                    {question.IsRequired ? ' *' : ''}
                  </label>
                  {question.QuestionType === 'textarea' && (
                    <textarea
                      rows={3}
                      value={answers[question.QuestionId] || ''}
                      onChange={(e) => handleAnswerChange(question.QuestionId, e.target.value)}
                    />
                  )}
                  {question.QuestionType === 'text' && (
                    <input
                      type="text"
                      value={answers[question.QuestionId] || ''}
                      onChange={(e) => handleAnswerChange(question.QuestionId, e.target.value)}
                    />
                  )}
                  {question.QuestionType === 'select' && (
                    <select
                      value={answers[question.QuestionId] || ''}
                      onChange={(e) => handleAnswerChange(question.QuestionId, e.target.value)}
                    >
                      <option value="">Select...</option>
                      {question.options.map(option => (
                        <option key={option.OptionId} value={option.OptionText}>{option.OptionText}</option>
                      ))}
                    </select>
                  )}
                  {question.QuestionType === 'radio' && (
                    <div className="rsvp-options">
                      {question.options.map(option => (
                        <label key={option.OptionId} className="option-item">
                          <input
                            type="radio"
                            name={`question-${question.QuestionId}`}
                            value={option.OptionText}
                            checked={answers[question.QuestionId] === option.OptionText}
                            onChange={(e) => handleAnswerChange(question.QuestionId, e.target.value)}
                          />
                          <span className="option-label">{option.OptionText}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  {question.QuestionType === 'checkbox' && (
                    <div className="rsvp-options">
                      {question.options.map(option => (
                        <label key={option.OptionId} className="option-item">
                          <input
                            type="checkbox"
                            value={option.OptionText}
                            checked={Array.isArray(answers[question.QuestionId]) && answers[question.QuestionId].includes(option.OptionText)}
                            onChange={(e) => handleCheckboxChange(question.QuestionId, option.OptionText, e.target.checked)}
                          />
                          <span className="option-label">{option.OptionText}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  {!QUESTION_TYPES_WITH_OPTIONS.includes(question.QuestionType) && question.QuestionType !== 'text' && question.QuestionType !== 'textarea' && (
                    <input
                      type="text"
                      value={answers[question.QuestionId] || ''}
                      onChange={(e) => handleAnswerChange(question.QuestionId, e.target.value)}
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="modal-actions" style={{ justifyContent: 'flex-start', gap: '10px' }}>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Submitting...' : 'Confirm RSVP'}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={submitting}
              onClick={handleDecline}
            >
              {submitting ? 'Submitting...' : 'Decline Invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventRsvp;
